import htmx from 'htmx.org';
import { Params } from '../Shared/scriptts/search-params';
import { getUserObject, setUserObject } from '../Shared/scriptts/session/sessionmain';
import { defaults, isEmpty } from '../Shared/scriptts/utilities';
import { logSaveJob, logSearchConversion } from '../Shared/scriptts/analytics';
import { Job } from '../Shared/scriptts/interfaces';

declare var job: Job;

document.addEventListener('DOMContentLoaded', () => {
    initSearchResultsNav();
    displayApplyButton();
    initJoaSaveButton();
    initHeaderObserver();
    bindEvents();
});

function initSearchResultsNav() {
    const userObject = getUserObject();
    const backToResultsElem = document.querySelector<HTMLAnchorElement>('#back-to-results');
    const navContainer = document.querySelector<HTMLElement>('#joa-prev-next');
    const prevElem = document.querySelector<HTMLAnchorElement>('#previous-joa');
    const nextElem = document.querySelector<HTMLAnchorElement>('#next-joa');

    if (!backToResultsElem || !navContainer || !prevElem || !nextElem || !userObject) {
        return;
    }

    if (!userObject.SearchResults?.Results?.ReturnUrl || (!document.referrer || document.referrer.toLowerCase().indexOf("/search") === -1 && document.referrer.toLowerCase().indexOf("/job") === -1)) {
        backToResultsElem.classList.add('hidden');
        navContainer.classList.add('hidden');
        return;
    }

    let jobsList = userObject.SearchResults?.Results?.Items || [];
    const currentJob = location.pathname.split('/').pop() || '';
    const currentJobPosition = currentJob === '' ? -1 : jobsList.indexOf(currentJob);
    let previousJob = jobsList[currentJobPosition - 1] || null;
    let nextJob = jobsList[currentJobPosition + 1] || null;
    const page = parseInt(userObject.SearchResults.Params.Page || '1')
    let returnUrl = new URL(userObject.SearchResults.Results.ReturnUrl);

    if (userObject.SearchResults.Params.Page) {
        returnUrl.searchParams.set('p', userObject.SearchResults.Params.Page);
    }

    backToResultsElem.href = returnUrl.toString();

    if (previousJob === null) {
        if (page < 2) {
            prevElem.classList.add('hidden');
        }
        else {
            getJobsFromIndex(userObject.SearchResults.Params, (page - 1).toString())
                .then(data => {
                    const dataJobsList = <string[]>JSON.parse(data);

                    jobsList = dataJobsList.concat(jobsList);
                    userObject.SearchResults.Results.Items = jobsList;
                    if (userObject.SearchResults.Results.PageSets.findIndex(p => p.Page === page - 1) === -1) {
                        userObject.SearchResults.Results.PageSets.push({ Page: page - 1, PageItems: dataJobsList });
                    }

                    setJobNavAttributes(prevElem, jobsList[jobsList.indexOf(currentJob) - 1]);
                    setUserObject(userObject);
                })
                .catch(err => console.error(err));
        }
    }
    else {
        setJobNavAttributes(prevElem, previousJob);
    }

    if (nextJob === null) {
        const totalPages = Math.ceil(userObject.SearchResults.Results.ResultCount / (userObject.SearchResults.Params.ResultsPerPage || defaults.resultsPerPage));

        if (page >= totalPages) {
            nextElem.classList.add('hidden');
        }
        else {
            getJobsFromIndex(userObject.SearchResults.Params, (page + 1).toString())
                .then(data => {
                    const dataJobsList = <string[]>JSON.parse(data);

                    jobsList = jobsList.concat(dataJobsList);
                    userObject.SearchResults.Results.Items = jobsList;
                    if (userObject.SearchResults.Results.PageSets.findIndex(p => p.Page === page + 1) === -1) {
                        userObject.SearchResults.Results.PageSets.push({ Page: page + 1, PageItems: dataJobsList });
                    }

                    setJobNavAttributes(nextElem, jobsList[jobsList.indexOf(currentJob) + 1]);
                    setUserObject(userObject);
                })
                .catch(err => console.error(err));
        }
    }
    else {
        setJobNavAttributes(nextElem, nextJob);
    }
}

function setJobNavAttributes(elem: HTMLAnchorElement, docid: string) {
    elem.href = `/job/${docid}`;
    elem.setAttribute('data-docid', docid);
    elem.addEventListener('click', resultsNavClicked);
}

async function getJobsFromIndex(params: Params, page: string) {
    const tempParams = structuredClone(params);
    tempParams.Page = page;
    return fetch('/search/executenextsearch',
        {
            method: 'post',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(tempParams)
        })
        .then(response => response.text());
}

function initJoaSaveButton() {
    displaySaveButton();
    document.querySelector<HTMLButtonElement>('#joa-save-button')?.addEventListener('click', saveJobClicked);
}

function displaySaveButton() {
    const userObject = getUserObject();
    const buttonElem = document.querySelector<HTMLButtonElement>('#joa-save-button');
    const iconElem = document.querySelector<HTMLElement>('#joa-save-icon');
    const textElem = document.querySelector<HTMLElement>('#joa-save-text');

    if (!userObject.IsAuthenticated || !buttonElem || !iconElem || !textElem) {
        return;
    }
    const docId = buttonElem.getAttribute('data-document-id') || '';
    const savedIconHref = '/img/icons/sprite.svg#star';
    const unsavedIconHref = '/img/icons/sprite.svg#star_outline';

    if (docId in userObject.SavedJobs) {
        buttonElem.setAttribute('data-state', 'is-saved');
        iconElem.querySelector('use')?.setAttribute('href', savedIconHref);
        textElem.innerText = 'Saved to My jobs';
    }
    else {
        buttonElem.setAttribute('data-state', 'is-unsaved');
        iconElem.querySelector('use')?.setAttribute('href', unsavedIconHref);
        textElem.innerText = 'Save';
    }
}

function applyClicked(evt: Event) {
    const applyElem = document.querySelector<HTMLAnchorElement>('#joa-apply-button');
    if (!applyElem) {
        return;
    }
    evt.preventDefault();
    
    logSearchConversion(getUserObject(), "search_conversion_apply");
    location.href = applyElem.href;
}

function displayApplyButton() {
    const applyElem = document.querySelector<HTMLElement>('#joa-apply-button');
    if (!applyElem) {
        return;
    }

    const userObject = getUserObject();
    applyElem.addEventListener('click', applyClicked, { once: true });

    if (userObject.IsAuthenticated) {
        const documentId = applyElem.getAttribute('data-document-id') || '';

        fetch(`/job/${documentId}/applytext`, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
            headers: { "Content-Type": "application/json" }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Response returned ${response.status}`);
                }
                return response.json();
            })
            .then(result => applyElem.innerText = result)
            .catch(reason => console.debug(reason));
    }
}

function saveJobClicked(evt: Event) {
    const elem = document.querySelector<HTMLButtonElement>('#joa-save-button');
    if (!elem) {
        return;
    }

    const documentId = elem.getAttribute('data-document-id') || '';
    const state = elem.getAttribute('data-state');

    if (state === 'is-saved') {
        deleteJob(documentId);
    }
    else if (state === 'is-unsaved') {
        saveJob(documentId);
    }
    else {
        console.debug('invalid save state');
    }
}

function saveJob(documentId: string) {
    fetch(`/Search/QuickSave/${documentId}`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: { "Content-Type": "application/json" }
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Response returned ${response.status}`);
            }
            return response.json();
        })
        .then(result => handleJobSaveResult(documentId, result))
        .then(displaySaveButton)
        .catch(reason => console.debug(reason));
}

function deleteJob(documentId: string) {
    fetch('/Applicant/Profile/DeleteSavedJob', {
        method: 'POST',
        headers: {
            'Accept': 'text/html',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
        },
        body: `job=${documentId}`
    })
        .then(response => {
            if (response.ok) {
                handleJobSaveResult(documentId, 'Job deleted');
            }
            else {
                throw new Error(`Response returned ${response.status}`);
            }
        })
        .then(displaySaveButton)
        .catch(reason => console.debug(reason));
}

function handleJobSaveResult(documentId: string, result: string) {
    const userObject = getUserObject();
   
    if (result === 'logged-out') {
        location.href = `/Applicant/Profile/QuickSave/${documentId}`;
    }
    else if (result === 'Job successfully saved.') {
        userObject.SavedJobs[documentId] = true;
        logSaveJob(job, true);
    }
    else if (result === 'Job deleted') {
        delete userObject.SavedJobs[documentId];
        logSaveJob(job, false);
    }
    else {
        console.debug(result);
    }
    setUserObject(userObject);
}

function initHeaderObserver() {
    const joaHeader = document.querySelector<HTMLElement>('#joa-header');
    const observerTarget = document.querySelector<HTMLElement>('#back-to-results-container');
    const applyMobile = document.querySelector<HTMLAnchorElement>('#joa-apply-mobile');
    const mobileSpacer = document.querySelector<HTMLElement>('#mobile-spacer');

    if (!joaHeader || !observerTarget || !applyMobile || !mobileSpacer) {
        return;
    }

    const observer = new IntersectionObserver((ioEntries) => {
        ioEntries.forEach(e => {
            if (e.isIntersecting) {
                joaHeader.classList.remove('hidden');
                applyMobile.classList.add('hidden');
                mobileSpacer.classList.add('hidden');
            }
            else {
                joaHeader.classList.add('hidden');
                applyMobile.classList.remove('hidden');
                mobileSpacer.classList.remove('hidden');
            }
        })
    }, {
        root: null,
        threshold: 1
    });
    observer.observe(observerTarget);
}

function resultsNavClicked(evt: Event) {
    const elem = <HTMLAnchorElement>(evt.currentTarget);
    const docid = elem.getAttribute('data-docid') || '';
    const userObject = getUserObject();

    if (!userObject || !userObject.SearchResults.Results.PageSets) {
        return;
    }

    for (let i = 0; i < userObject.SearchResults.Results.PageSets.length; i++) {
        if (userObject.SearchResults.Results.PageSets[i].PageItems.indexOf(docid) >= 0) {
            userObject.SearchResults.Params.Page = userObject.SearchResults.Results.PageSets[i].Page.toString();
            setUserObject(userObject);
            return;
        }
    }
}

function bindEvents() {
    addEventListener('pageshow', hashChanged);
    addEventListener('hashchange', hashChanged);
    htmx.on('htmx:afterSwap', () => {
        initJoaSaveButton();
    });
}

function hashChanged() {
    const defaultCss = 'button button-unstyled no-underline py-4 px-6 text-sm';
    const activeCss = ' font-bold text-black border-b-4 border-black';
    const hash = window.location.hash;

    if (!hash) {
        return;
    }

    document.querySelectorAll<HTMLAnchorElement>('[data-joa-nav]').forEach(elem => {
        elem.className = defaultCss;

        if (elem.href.substring(elem.href.lastIndexOf('#')) === hash.trim()) {
            elem.className += activeCss;
        }
    });

    const target = document.querySelector<HTMLElement>(hash);
    if (!target) {
        return;
    }

    const offset = 225;
    scrollTo({
        top: target.offsetTop - offset,
        behavior: 'instant'
    });
}
